'use client';

/**
 * NotificationDropdown Component
 *
 * Apple Liquid Glass dropdown panel that appears below the NotificationBell.
 * Shows a scrollable list of recent notifications with mark-all-read, filter
 * tabs (All / Mentions / Astor), and a "View all" footer.
 *
 * @deprecated Prefer `NotificationsPopover` for new code — that component
 *   implements the v3 topbar spec (mint border + inset-glow shell, tone-coded
 *   left-border accents per notification, controlled filter state). This
 *   dropdown remains for portals that haven't migrated yet (21-consumer-portal
 *   at the time of this annotation) and will be removed once they have.
 * @see Issue #1866 — v3 Notifications popover
 * @see Issue #699 - Notification System Fidelity
 * @see Apple WWDC 2025 - Liquid Glass design language
 * @see /tmp/design-fetch/fiops/project/shell.jsx NotificationsPopover — v3 design spec
 */

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { baseColors } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { springPresets } from '../../theme/transitions';
import { GlassButton } from '../GlassButton';
import { LiquidGlassHighlight } from '../LiquidGlassHighlight';
import type { NotificationItemData } from '../NotificationItem';
import { NotificationItem } from '../NotificationItem';
import { ShimmerLoader } from '../ShimmerLoader';

// ============================================================================
// Types
// ============================================================================

export type NotificationDropdownFilter = 'all' | 'mentions' | 'astor';

export interface NotificationDropdownProps {
  /** Whether the dropdown is open */
  open: boolean;

  /** Anchor element for positioning */
  anchorEl: HTMLElement | null;

  /** Called when dropdown should close */
  onClose: () => void;

  /** Notification items to display */
  notifications: NotificationItemData[];

  /** Whether notifications are loading */
  loading?: boolean;

  /** Called when "View All" is clicked */
  onViewAll?: () => void;

  /** Called when "Mark all as read" is clicked */
  onMarkAllRead?: () => void;

  /** Called when a notification item is clicked */
  onNotificationClick?: (notification: NotificationItemData) => void;

  /** Called when toggle read is triggered */
  onToggleRead?: (notification: NotificationItemData) => void;

  /** Called when archive is triggered */
  onArchive?: (notification: NotificationItemData) => void;

  /** Maximum items to show before "View All" */
  maxItems?: number;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Filter notifications by tab. Currently keyed off two fields on
 * `NotificationItemData`:
 *   - 'astor'    → `type === 'agent'` (Astor agent–originated notifications).
 *   - 'mentions' → `isMention === true` (optional flag — backend may not set it
 *                  on every notification yet, in which case Mentions degrades
 *                  to the standard empty-state copy).
 */
function filterNotifications(
  notifications: NotificationItemData[],
  filter: NotificationDropdownFilter,
): NotificationItemData[] {
  if (filter === 'all') return notifications;
  if (filter === 'astor') {
    // Astor-originated notifications use NotificationType.AGENT ('agent').
    return notifications.filter((n) => String(n.type).toLowerCase() === 'agent');
  }
  // 'mentions' — when the DTO doesn't carry isMention yet (or all flags are
  // false), the result is an empty array and the empty-state copy renders.
  return notifications.filter((n) => n.isMention === true);
}

// ============================================================================
// Component
// ============================================================================

export const NotificationDropdown = forwardRef<HTMLDivElement, NotificationDropdownProps>(
  (
    {
      open,
      anchorEl,
      onClose,
      notifications,
      loading = false,
      onViewAll,
      onMarkAllRead,
      onNotificationClick,
      onToggleRead,
      onArchive,
      maxItems = 5,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { t } = useI18n();
    const glass = isDark ? glassEffects.dark : glassEffects.light;

    const [activeFilter, setActiveFilter] = useState<NotificationDropdownFilter>('all');

    const hasUnread = notifications.some((n) => !n.isRead);
    const totalCount = notifications.length;

    const filteredNotifications = useMemo(
      () => filterNotifications(notifications, activeFilter),
      [notifications, activeFilter],
    );
    const displayItems = filteredNotifications.slice(0, maxItems);
    const hasMore = filteredNotifications.length > maxItems;

    const handleMarkAllRead = useCallback(() => {
      onMarkAllRead?.();
    }, [onMarkAllRead]);

    const mintMain = baseColors.success[500];

    const filterTabs: Array<{ key: NotificationDropdownFilter; label: string; showCount?: boolean }> = [
      { key: 'all', label: t('notification.dropdown.filter.all'), showCount: true },
      { key: 'mentions', label: t('notification.dropdown.filter.mentions') },
      { key: 'astor', label: t('notification.dropdown.filter.astor') },
    ];

    return (
      <Popover
        ref={ref}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 380,
              maxHeight: 520,
              borderRadius: 3,
              overflow: 'hidden',
              // Liquid Glass surface
              background: isDark ? 'rgba(20, 20, 30, 0.85)' : 'rgba(255, 255, 255, 0.88)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              border: `0.5px solid ${glass.border}`,
              boxShadow: isDark
                ? '0 20px 60px rgba(0,0,0,0.5), 0 8px 20px rgba(0,0,0,0.3), inset 0 0.5px 0 rgba(255,255,255,0.06)'
                : '0 20px 60px rgba(0,0,0,0.12), 0 8px 20px rgba(0,0,0,0.06), inset 0 0.5px 0 rgba(255,255,255,0.7)',
            },
          },
        }}
      >
        {/* Specular highlight */}
        <Box sx={{ position: 'relative' }}>
          <LiquidGlassHighlight intensity="subtle" borderRadius={12} />

          {/* Header — title (left) + Mark-all-read text button (right) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 1.5,
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              {t('notification.dropdown.title')}
            </Typography>
            {hasUnread && onMarkAllRead && (
              <GlassButton
                variant="text"
                size="small"
                onClick={handleMarkAllRead}
                sx={{
                  fontWeight: 500,
                  color: 'text.secondary',
                  textTransform: 'none',
                  minWidth: 0,
                  py: 0.25,
                  px: 0.75,
                  '&:hover': {
                    color: 'text.primary',
                    background: 'transparent',
                  },
                }}
              >
                {t('notification.dropdown.markAllRead')}
              </GlassButton>
            )}
          </Box>

          {/* Filter tab row — All · {count} / Mentions / Astor */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              px: 1.25,
              pb: 1,
              position: 'relative',
              zIndex: 2,
            }}
          >
            {filterTabs.map((tab) => {
              const isActive = activeFilter === tab.key;
              return (
                <Box
                  component="button"
                  type="button"
                  key={tab.key}
                  aria-pressed={isActive}
                  onClick={() => setActiveFilter(tab.key)}
                  sx={{
                    flex: 1,
                    py: 0.625,
                    px: 1,
                    borderRadius: 1.25,
                    border: 'none',
                    cursor: 'pointer',
                    typography: 'caption',
                    fontWeight: isActive ? 600 : 500,
                    bgcolor: isActive ? alpha(mintMain, isDark ? 0.18 : 0.12) : 'transparent',
                    color: isActive ? 'success.main' : 'text.secondary',
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                    '&:hover': {
                      bgcolor: isActive
                        ? alpha(mintMain, isDark ? 0.22 : 0.16)
                        : isDark
                          ? 'rgba(255,255,255,0.04)'
                          : 'rgba(0,0,0,0.03)',
                      color: isActive ? 'success.main' : 'text.primary',
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${alpha(mintMain, 0.5)}`,
                      outlineOffset: 1,
                    },
                  }}
                >
                  {tab.showCount ? `${tab.label} · ${totalCount}` : tab.label}
                </Box>
              );
            })}
          </Box>

          <Divider sx={{ opacity: isDark ? 0.08 : 0.12 }} />

          {/* Notification list */}
          <Box
            sx={{
              maxHeight: 360,
              overflowY: 'auto',
              overflowX: 'hidden',
              // Custom scrollbar (Liquid Glass style)
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
                borderRadius: 2,
              },
            }}
          >
            {loading ? (
              <Box sx={{ px: 2.5, py: 2 }}>
                {[0, 1, 2].map((i) => (
                  <Box key={i} sx={{ mb: 1.5 }}>
                    <ShimmerLoader sx={{ height: 56, borderRadius: 2 }} />
                  </Box>
                ))}
              </Box>
            ) : displayItems.length === 0 ? (
              <Box
                sx={{
                  py: 5,
                  px: 3,
                  textAlign: 'center',
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={springPresets.gentle}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    {t('notification.dropdown.empty')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {t('notification.dropdown.emptyDescription')}
                  </Typography>
                </motion.div>
              </Box>
            ) : (
              <AnimatePresence mode="popLayout">
                {displayItems.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClick={onNotificationClick}
                    onToggleRead={onToggleRead}
                    onArchive={onArchive}
                    compact
                  />
                ))}
              </AnimatePresence>
            )}
          </Box>

          {/* Footer */}
          {(hasMore || onViewAll) && displayItems.length > 0 && (
            <>
              <Divider sx={{ opacity: isDark ? 0.08 : 0.12 }} />
              <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
                <GlassButton
                  variant="text"
                  size="small"
                  onClick={onViewAll}
                  sx={{
                    fontWeight: 500,
                    color: 'primary.main',
                    textTransform: 'none',
                  }}
                >
                  {t('notification.dropdown.viewAll')}
                </GlassButton>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    );
  },
);

NotificationDropdown.displayName = 'NotificationDropdown';

export default NotificationDropdown;
